
import Vue from 'vue';
import BigPictureWrapper from '@gallery/components/BigPictureWrapper.vue';
import { ImageItem } from '@gallery/types/Gallery';
import ThumbWrapper from '@gallery/components/thumb/ThumbWrapper.vue';
import LightboxInfo from './LightboxInfo.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import BaseIcon from '@core/components/UI/BaseIcon.vue';
import config from '@gallery/config/gallery.config';
import trackPageView from '@gallery/utils/trackPageView';

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element;
  mozFullScreenElement?: Element;
  msFullscreenElement?: Element;
  webkitExitFullscreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
};

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

interface Data {
  config: Record<string, any>;
  activePictureIndex: number;
  changedPicturesCount: number;
  nextAd: {
    banner: string;
    height: number;
    width: number;
  } | null;
  showThumbWrapper: boolean;
  showMobileDescription: boolean;
  navigationDisabled: boolean;
  galleryTowerKey: string;
  isFullscreen: boolean;
  previouslyFocusedElement: HTMLElement | null;
}

interface Methods {
  setActivePictureIndex: (index: number) => Promise<void>;
  addPictureRelatedHash: () => void;
  keyboardNavigation: (e: KeyboardEvent) => void;
  toggleThumbWrapper: () => void;
  setMobileDescription: (show: boolean) => void;
  getFullscreenState: () => boolean;
  updateFullscreenState: () => void;
  toggleFullscreen: (element: FullscreenElement) => void;
  getFocusableElements: () => HTMLElement[];
}

interface Computed {
  activePicture: ImageItem;
  adsAttributes: {
    mkw: string[];
    mkv: Record<string, string>;
  };
  activePictureDescription: string;
  authorsList: string;
  ariaLabel: string;
  closeLabel: string;
}

interface Props {
  pictures: ImageItem[];
  initialIndex: number;
  galleryId: string;
  galleryTitle: string;
  settings: Record<string, any>;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BigPictureWrapper,
    ThumbWrapper,
    LightboxInfo,
    AdFormBanner,
    BaseIcon,
  },
  props: {
    pictures: {
      type: Array,
      required: true,
    },
    initialIndex: {
      type: Number,
      required: false,
      default: 0,
    },
    galleryId: {
      type: String,
      required: false,
    },
    galleryTitle: {
      type: String,
      required: false,
    },
    settings: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      config,
      activePictureIndex: this.initialIndex,
      changedPicturesCount: 0,
      nextAd: null,
      showThumbWrapper: true,
      showMobileDescription: true,
      navigationDisabled: false,
      galleryTowerKey: 'gt-0',
      isFullscreen: false,
      previouslyFocusedElement: null,
    };
  },
  computed: {
    activePicture() {
      return this.pictures[this.activePictureIndex];
    },
    adsAttributes() {
      const keywords: string[] = this.$store.getters['adForm/getAdsKeywords'];
      const keyValues: Record<string, string> = this.$store.getters['adForm/getAdsKeyValues'];

      return {
        mkw: keywords,
        mkv: keyValues,
      };
    },
    activePictureDescription() {
      return this.activePicture?.metadata.credit ? this.activePicture?.metadata.credit : '';
    },
    authorsList(): string {
      return this.activePicture?.metadata.authors ? this.activePicture?.metadata.authors.join(', ') : '';
    },
    ariaLabel(): string {
      return this.galleryTitle || (this.$t('portal_core.photos') as string);
    },
    closeLabel(): string {
      return this.$t('words.close') as string;
    },
  },
  methods: {
    async setActivePictureIndex(index) {
      if (index >= this.pictures.length) {
        this.activePictureIndex = 0;
      } else if (index < 0) {
        this.activePictureIndex = this.pictures.length - 1;
      } else {
        this.activePictureIndex = index;
      }

      this.addPictureRelatedHash();

      // Track analytics page view
      trackPageView({ uid: this.settings?.analytics?.gemius, cxid: this.settings?.analytics?.cxense, ctx: this });
    },
    addPictureRelatedHash() {
      const hash = new URLSearchParams();
      hash.append('gallery-id', this.galleryId);
      hash.append('image-id', this.activePicture.id);

      history.replaceState(null, '', `#${hash.toString()}`);
    },
    keyboardNavigation(e) {
      if (e.key === 'Tab') {
        const focusableElements = this.getFocusableElements();
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement as HTMLElement | null;

        if (!focusableElements.length) {
          e.preventDefault();
          (this.$refs.lightbox as HTMLElement).focus();
        } else if (e.shiftKey && activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }

        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          this.setActivePictureIndex(this.activePictureIndex - 1);
          break;
        case 'ArrowRight':
          this.setActivePictureIndex(this.activePictureIndex + 1);
          break;
        case 'Escape':
          this.$emit('close-lightbox');
          break;
      }
    },
    toggleThumbWrapper() {
      this.showThumbWrapper = !this.showThumbWrapper;
    },
    setMobileDescription(show) {
      this.showMobileDescription = show;
    },
    getFullscreenState(): boolean {
      const doc: FullscreenDocument = document;

      return !!(doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement);
    },
    updateFullscreenState() {
      this.isFullscreen = this.getFullscreenState();
    },
    toggleFullscreen(element = document.documentElement) {
      const doc: FullscreenDocument = document;

      if (!this.getFullscreenState()) {
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }
      } else {
        if (doc.exitFullscreen) {
          doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          doc.webkitExitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          doc.mozCancelFullScreen();
        } else if (doc.msExitFullscreen) {
          doc.msExitFullscreen();
        }
      }
    },
    getFocusableElements() {
      const lightbox = this.$refs.lightbox as HTMLElement | undefined;
      if (!lightbox) {
        return [];
      }

      return (Array.from(lightbox.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')) as HTMLElement[]).filter(
        (element) => element.getClientRects().length > 0
      );
    },
  },
  mounted() {
    this.previouslyFocusedElement = document.activeElement as HTMLElement | null;
    this.addPictureRelatedHash();

    document.addEventListener('fullscreenchange', this.updateFullscreenState);
    document.addEventListener('webkitfullscreenchange', this.updateFullscreenState);
    document.addEventListener('mozfullscreenchange', this.updateFullscreenState);
    document.addEventListener('MSFullscreenChange', this.updateFullscreenState);

    document.addEventListener('keydown', this.keyboardNavigation);

    this.$nextTick(() => {
      const firstElement = this.getFocusableElements()[0];
      (firstElement || (this.$refs.lightbox as HTMLElement | undefined))?.focus();
    });

    // Track first image analytics page view
    trackPageView({ uid: this.settings?.analytics?.gemius, cxid: this.settings?.analytics?.cxense, ctx: this });
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.updateFullscreenState);
    document.removeEventListener('webkitfullscreenchange', this.updateFullscreenState);
    document.removeEventListener('mozfullscreenchange', this.updateFullscreenState);
    document.removeEventListener('MSFullscreenChange', this.updateFullscreenState);

    document.removeEventListener('keydown', this.keyboardNavigation);
    this.previouslyFocusedElement?.focus?.();
  },
});
